import { log, reason } from '../app/log.js'
import { db } from '../state/db.js'
import { activeChildId } from '../state/device.js'
import { applyReviewCode } from './api.js'
import { appendParentEvent } from './children.js'

/**
 * Review mode, the one step full access for a professor or a tester (PROGRESS, operator
 * decision of 2026-09-06). Three doors lead here and they all end in the same place: the
 * server checks the code, then the device appends `review_mode_set` so the local fold unlocks
 * every region offline as well. The event carries the review tag, so the study never counts
 * it.
 *
 * The doors are the `?inceleme=<code>` boot link, and the switch in the parent settings, which
 * asks for the same code through the same endpoint. Turning it off needs no code: it takes
 * nothing away that the parent did not already have.
 */

const logger = log('parent-review')

/** The settings switch and the boot link share this. Throws when the server refuses the code. */
export async function enableReviewMode(childId: string, code: string): Promise<void> {
  await applyReviewCode(childId, code)
  await appendParentEvent(childId, 'review_mode_set', { on: true }, true)
}

/** Turning it back off. No code: a parent may always close a door they opened. */
export async function disableReviewMode(childId: string): Promise<void> {
  await appendParentEvent(childId, 'review_mode_set', { on: false }, true)
}

/**
 * The boot hook for `?inceleme=<code>` on any URL (WP-F owns this, `app/src/app/` calls it).
 * The tester opens the link on the phone that already carries the child, so the code lands on
 * the active child, or on every child this device knows when none is active. The parameter is
 * dropped from the address afterwards, whether or not the server accepted it, so a shared
 * screenshot of the URL bar does not hand the code on.
 */
export async function applyReviewQuery(): Promise<boolean> {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  const code = params.get('inceleme')?.trim() ?? ''
  strip(params)
  if (code.length < 4) return false

  let applied = false
  for (const childId of await targets()) {
    try {
      await enableReviewMode(childId, code)
      applied = true
    } catch (error) {
      logger.warn(`the review code was refused for child ${childId}`, reason(error))
    }
  }
  if (!applied) logger.warn('the review code arrived before any child existed on this device')
  return applied
}

async function targets(): Promise<string[]> {
  const active = await activeChildId()
  if (active) return [active]
  const keys = await db.events.orderBy('childId').uniqueKeys()
  return keys.map((key) => String(key))
}

function strip(params: URLSearchParams): void {
  params.delete('inceleme')
  const query = params.toString()
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
  )
}
